import React, {
  useEffect, useState, useCallback, useMemo, memo
} from "react"
import { useSelector } from "react-redux"
import {
  format, addDays, parseISO
} from "date-fns"
import BeatLoader from "react-spinners/BeatLoader"
import { useDroppable } from "@dnd-kit/core"

import { getSurveyorSchedule } from "requesters/operationScheduleRequester"
import { getBaseUrl } from "../../reducers"
import TargetMetricsRow from "./TargetMetricsRow"
import SuggestionRow from "./SuggestionRow"
import JobServiceGroup from "./JobServiceGroup"
import LeaveItem from "./LeaveItem"
import DroppableDayCell from "./DroppableDayCell"
import { isSurveyorAvailable, groupJobServicesByAddressAndTime } from "./utils"

// Days of the week for the schedule
const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

// Loading states enum for better state management
const LOADING_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error"
}

/**
 * Extract error message from API error response
 * @param {Error|Object} error - Error object from API call
 * @returns {string} Human-readable error message
 */
const extractErrorMessage = error => {
  if (!error) return "Failed to load schedule data"

  if (error?.response?.data?.error) {
    return error.response.data.error
  }
  if (error?.response?.data?.errors?.[0]) {
    return error.response.data.errors[0]
  }
  if (error?.message) {
    return error.message
  }

  return "Failed to load schedule data"
}

/**
 * SurveyorScheduleBlock component displays a single surveyor's weekly schedule.
 * It shows the surveyor name, working hours for each day, job services, leaves,
 * and total job count. It is also a drop target for cross-surveyor reassignment.
 *
 * @param {Object} props
 * @param {Object} props.surveyor - Surveyor data with id and attributes
 * @param {Date} props.weekStartDate - Start date of the week (Monday)
 * @param {Date} props.weekEndDate - End date of the week (Saturday)
 * @param {Object} props.publicHolidaysByDate - Public holidays data indexed by date
 * @param {Object|undefined} props.scheduleData - Pre-fetched schedule data from the
 *   parent (parallel fetch). When provided the component skips its own API call.
 *   Pass `undefined` to let the component self-fetch (fallback).
 * @param {Function} props.onRefresh - Callback to trigger refresh after assignment
 * @param {boolean} props.isUpdating - Whether an update operation is in progress
 */
function SurveyorScheduleBlock({
  surveyor, weekStartDate, weekEndDate, publicHolidaysByDate, scheduleData: parentScheduleData, refreshTrigger, onRefresh, isUpdating
}) {
  // refreshTrigger is used as a dep in the sync effect below as a safety net
  // to force re-sync even if parentScheduleData reference didn't change.
  const baseUrl = useSelector(getBaseUrl)

  // Sentinel values for parentScheduleData:
  //   undefined  → self-fetch mode (fallback, parent not managing)
  //   null       → parent is managing but still loading — show spinner
  //   object     → parent has data — render immediately
  //
  // hasParentData covers both null and object: as long as it's not undefined
  // the self-fetch path is completely bypassed.
  const hasParentData = parentScheduleData !== undefined

  const [scheduleData, setScheduleData] = useState(() => (
    // null means loading, so don't treat it as actual data on init
    hasParentData && parentScheduleData !== null ? parentScheduleData : null
  ))
  const [loadingState, setLoadingState] = useState(() => {
    if (!hasParentData) return LOADING_STATES.IDLE          // self-fetch, idle until effect fires
    if (parentScheduleData === null) return LOADING_STATES.LOADING // parent fetching
    return LOADING_STATES.SUCCESS                            // parent has data
  })
  const [error, setError] = useState(null)

  // Derived loading state for backwards compatibility
  const loading = loadingState === LOADING_STATES.LOADING

  // Memoize surveyor attributes to prevent unnecessary re-renders
  const { attributes } = surveyor
  const surveyorName = useMemo(() => attributes?.name || "Unknown Surveyor", [attributes?.name])
  const workingSchedule = useMemo(() => attributes?.working_schedule || {}, [attributes?.working_schedule])

  // Set up droppable for the entire surveyor block (for cross-surveyor reassignment)
  const droppableId = `surveyor-block-${surveyor.id}`
  const { isOver, setNodeRef, active } = useDroppable({
    id: droppableId,
    data: {
      surveyorId: surveyor.id,
      surveyorName,
      workingSchedule,
      type: "surveyor-block"
    }
  })

  // Check if the dragged item is from a different surveyor.
  // `active` changes on every pointermove, so we intentionally do NOT put it in any
  // dep array.  We read it inline and only re-run the memo when isOver or the
  // schedule/working data actually changes.
  const isDraggingFromOtherSurveyor = active != null && active?.data?.current?.surveyorId !== surveyor.id

  // Availability is only relevant when the pointer is hovering over this block AND
  // the drag originates from a different surveyor.  Skip the whole computation
  // otherwise so we don't do work on every pointer-move event.
  const availability = useMemo(() => {
    if (!isOver || !isDraggingFromOtherSurveyor || !active?.data?.current) {
      return { available: true, reason: null }
    }

    const { jobService } = active.data.current
    const bookingTime = jobService?.booking_time || jobService?.bookingTime

    if (!bookingTime) {
      return { available: false, reason: "No booking time" }
    }

    const bookingDate = typeof bookingTime === "string" ? new Date(bookingTime) : bookingTime
    const blockOuts = scheduleData?.days
      ? Object.values(scheduleData.days).flatMap(day => day.block_outs || [])
      : []

    const surveyorForCheck = {
      id: surveyor.id,
      workingSchedule,
      working_schedule: workingSchedule
    }

    const isAvailable = isSurveyorAvailable(surveyorForCheck, bookingDate, blockOuts)

    if (!isAvailable) {
      return { available: false, reason: `${surveyorName} is not available on this day` }
    }

    return { available: true, reason: null }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOver, isDraggingFromOtherSurveyor, scheduleData, workingSchedule, surveyor.id, surveyorName])

  // When the parent updates parentScheduleData (e.g. after a drag refresh),
  // sync it into local state.
  // null = still loading (already handled by initial loadingState above)
  // object = data arrived — update local state
  //
  // refreshTrigger is included as a safety net: if the parent bumps the trigger
  // (via surveyorRefreshKeys) we force re-sync even if the data reference is
  // somehow unchanged (defensive against object identity edge cases).
  useEffect(() => {
    if (parentScheduleData === undefined) return // self-fetch mode, ignore
    if (parentScheduleData === null) {
      // Parent is (re-)loading, e.g. week changed — show spinner
      setLoadingState(LOADING_STATES.LOADING)
    } else {
      setScheduleData(parentScheduleData)
      setLoadingState(LOADING_STATES.SUCCESS)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentScheduleData, refreshTrigger])

  // Fetch schedule data for this surveyor — only runs when the parent did NOT
  // provide data (fallback self-fetch path).
  const fetchScheduleData = useCallback(async () => {
    setLoadingState(LOADING_STATES.LOADING)
    setError(null)

    try {
      const startDateStr = format(weekStartDate, "yyyy-MM-dd")
      const endDateStr = format(weekEndDate, "yyyy-MM-dd")

      const result = await getSurveyorSchedule(
        baseUrl,
        surveyor.id,
        startDateStr,
        endDateStr
      )

      if (result.status === 200 && result.response?.data) {
        setScheduleData(result.response.data)
        setLoadingState(LOADING_STATES.SUCCESS)
      } else if (result.error) {
        const errorMessage = extractErrorMessage(result.error)
        setError(errorMessage)
        setLoadingState(LOADING_STATES.ERROR)
        console.error("Error fetching schedule:", result.error)
      } else {
        // Empty response but no error
        setScheduleData(null)
        setLoadingState(LOADING_STATES.SUCCESS)
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      setError(errorMessage)
      setLoadingState(LOADING_STATES.ERROR)
      console.error("Error fetching schedule:", err)
    }
  }, [baseUrl, surveyor.id, weekStartDate, weekEndDate])

  useEffect(() => {
    // Skip self-fetch when the parent is supplying the data
    if (hasParentData) return
    fetchScheduleData()
  }, [fetchScheduleData, hasParentData])

  // Get total job count from schedule data or calculate from days - memoized
  const totalJobCount = useMemo(() => {
    if (scheduleData?.total_job_count !== undefined) {
      return scheduleData.total_job_count
    }

    // Fallback: count from days data
    if (scheduleData?.days) {
      let count = 0
      Object.values(scheduleData.days).forEach(dayData => {
        count += dayData.job_services?.length || 0
      })
      return count
    }

    return 0
  }, [scheduleData])

  // Format working hours for display - memoized callback
  // Converts Sydney time to user's local timezone
  const formatWorkingHours = useCallback(daySchedule => {
    if (!daySchedule?.works) {
      return null
    }

    const startTime = daySchedule.start_time || ""
    const endTime = daySchedule.end_time || ""

    if (!startTime || !endTime) {
      return "Working"
    }

    try {
      // Parse times as Sydney time and convert to user's local timezone
      // Create a date object for today in Sydney timezone (AEDT/AEST is UTC+11/+10)
      // We'll use a reference date to convert the times
      const referenceDate = new Date()
      const year = referenceDate.getFullYear()
      const month = referenceDate.getMonth() + 1
      const day = referenceDate.getDate()

      // Parse start time (e.g., "09:00")
      const [startHour, startMin] = startTime.split(":").map(Number)
      // Create ISO string assuming Sydney timezone (we'll use +11:00 as approximation)
      // Note: This is a simplification. Ideally we'd use a timezone library for DST handling
      const startISO = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}:00+11:00`
      const startDate = parseISO(startISO)

      // Parse end time (e.g., "17:00")
      const [endHour, endMin] = endTime.split(":").map(Number)
      const endISO = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}:00+11:00`
      const endDate = parseISO(endISO)

      // Format in user's local timezone
      const formattedStart = format(startDate, "HH:mm")
      const formattedEnd = format(endDate, "HH:mm")

      return `${formattedStart} - ${formattedEnd}`
    } catch (error) {
      console.error("Error formatting working hours:", error)
      // Fallback to original format if conversion fails
      return `${startTime} - ${endTime}`
    }
  }, [])

  // Get job services for a specific day - memoized callback
  const getJobServicesForDay = useCallback(dayIndex => {
    if (!scheduleData?.days) return []

    const dayDate = addDays(weekStartDate, dayIndex)
    const dateKey = format(dayDate, "yyyy-MM-dd")
    const dayData = scheduleData.days[dateKey]

    return dayData?.job_services || []
  }, [scheduleData, weekStartDate])

  // Get warning status for a specific day - memoized callback
  const getWarningForDay = useCallback(dayIndex => {
    if (!scheduleData?.days) return false

    const dayDate = addDays(weekStartDate, dayIndex)
    const dateKey = format(dayDate, "yyyy-MM-dd")
    const dayData = scheduleData.days[dateKey]

    return dayData?.show_warning || false
  }, [scheduleData, weekStartDate])

  // Get block outs for a specific day - memoized callback
  const getBlockOutsForDay = useCallback(dayIndex => {
    if (!scheduleData?.days) return []

    const dayDate = addDays(weekStartDate, dayIndex)
    const dateKey = format(dayDate, "yyyy-MM-dd")
    const dayData = scheduleData.days[dateKey]

    return dayData?.block_outs || []
  }, [scheduleData, weekStartDate])

  // Pre-compute grouped job services for every day in one pass when scheduleData changes.
  // Without this, renderJobServicesForDay sorts + groups on every render for each of 6 days (Fix #6).
  const groupedServicesPerDay = useMemo(() => {
    const result = {}
    if (!scheduleData?.days) return result

    for (let dayIndex = 0; dayIndex < 6; dayIndex++) {
      const dayDate = addDays(weekStartDate, dayIndex)
      const dateKey = format(dayDate, "yyyy-MM-dd")
      const jobServices = scheduleData.days[dateKey]?.job_services || []

      const sorted = [...jobServices].sort((a, b) => {
        const timeA = a.booking_time || a.bookingTime || "09:00"
        const timeB = b.booking_time || b.bookingTime || "09:00"
        return timeA.localeCompare(timeB)
      })

      result[dayIndex] = groupJobServicesByAddressAndTime(sorted)
    }
    return result
  }, [scheduleData, weekStartDate])

  // Render job services for a day — reads from the pre-computed map (Fix #6)
  const renderJobServicesForDay = useCallback(dayIndex => {
    const groupedServices = groupedServicesPerDay[dayIndex]
    if (!groupedServices?.length) return null

    return groupedServices.map(group => (
      <JobServiceGroup
        key={group.groupKey || group.primaryService.id}
        group={group}
        surveyorId={surveyor.id}
        dayIndex={dayIndex} />
    ))
  }, [groupedServicesPerDay, surveyor.id])

  // Render block outs/leave items for a day - memoized callback
  const renderBlockOutsForDay = useCallback(dayIndex => {
    const blockOuts = getBlockOutsForDay(dayIndex)
    if (blockOuts.length === 0) return null

    return blockOuts.map(blockOut => (
      <LeaveItem
        key={blockOut.id}
        blockOut={blockOut} />
    ))
  }, [getBlockOutsForDay])

  // Render day cell content with job services and leave items - memoized callback
  const renderDayCellContent = useCallback(dayIndex => {
    if (loading) {
      return (
        <div className="loading-cell">
          <BeatLoader color="#213F7A" size={8} loading />
        </div>
      )
    }

    if (error) {
      return (
        <div className="error-cell">
          <span className="text-danger" title={error}>Error</span>
        </div>
      )
    }

    const jobServices = getJobServicesForDay(dayIndex)
    const blockOuts = getBlockOutsForDay(dayIndex)

    if (jobServices.length === 0 && blockOuts.length === 0) {
      return (
        <div className="empty-cell">-</div>
      )
    }

    return (
      <div className="day-schedule-content">
        {renderJobServicesForDay(dayIndex)}
        {renderBlockOutsForDay(dayIndex)}
      </div>
    )
  }, [loading, error, getJobServicesForDay, getBlockOutsForDay, renderJobServicesForDay, renderBlockOutsForDay])

  return (
    <div
      ref={setNodeRef}
      className={`surveyor-schedule-block mb-3 ${isOver && isDraggingFromOtherSurveyor ? "drop-target-surveyor" : ""} ${isOver && isDraggingFromOtherSurveyor && !availability.available ? "drop-target-unavailable" : ""}`}>
      {/* Show availability warning when dragging over unavailable surveyor */}
      {isOver && isDraggingFromOtherSurveyor && !availability.available && (
        <div className="availability-warning">
          <span className="warning-icon" role="img" aria-label="Warning">⚠️</span>
          {availability.reason}
        </div>
      )}
      <table className="table table-bordered surveyor-block-table">
        <tbody>
          {/* Header row with surveyor name and working hours */}
          <tr className="surveyor-header-row">
            <td className="surveyor-name-col">
              <strong>{surveyorName}</strong>
            </td>
            {WEEK_DAYS.map((day, dayIndex) => {
              const dayKey = day.toLowerCase()
              const daySchedule = workingSchedule[dayKey]
              const isWorking = daySchedule?.works
              const workingHours = formatWorkingHours(daySchedule)
              const showWarning = getWarningForDay(dayIndex)

              return (
                <td key={day} className="day-col day-header-cell">
                  {isWorking ? (
                    <div className="working-hours-container">
                      {showWarning && (
                        <span
                          className="warning-icon-header"
                          role="img"
                          aria-label="Warning"
                          title="Surveyor has completed 70% of estimated hours but has more than 4 hours until end of day">
                          ⚠️
                        </span>
                      )}
                      <span className="working-hours">{workingHours}</span>
                    </div>
                  ) : (
                    <span className="not-working">Not Working</span>
                  )}
                </td>
              )
            })}
            <td className="total-col">
              <span className="job-count">{totalJobCount}</span>
            </td>
          </tr>

          {/* Content row with job services and leave items */}
          <tr className="surveyor-content-row">
            <td className="surveyor-name-col">
              {/* Placeholder for additional info */}
            </td>
            {WEEK_DAYS.map((day, dayIndex) => {
              const dayKey = day.toLowerCase()
              const daySchedule = workingSchedule[dayKey]
              const isWorking = daySchedule?.works
              const dayDate = addDays(weekStartDate, dayIndex)

              if (!isWorking) {
                return (
                  <td key={day} className="day-col not-working-cell">
                    <span className="not-working-text">Not Working</span>
                  </td>
                )
              }

              return (
                <DroppableDayCell
                  key={day}
                  dayIndex={dayIndex}
                  surveyorId={surveyor.id}
                  date={dayDate}
                  publicHolidaysByDate={publicHolidaysByDate}
                  className="day-col schedule-cell">
                  {renderDayCellContent(dayIndex)}
                </DroppableDayCell>
              )
            })}
            <td className="total-col">
              {/* Total column content - placeholder for now */}
            </td>
          </tr>

          {/* Metrics row showing target hours/dollars and actuals */}
          {!loading && scheduleData?.metrics && (
            <>
              {/* Suggestion row for adding job services */}
              <SuggestionRow
                surveyorId={surveyor.id}
                surveyorName={surveyorName}
                weekStartDate={weekStartDate}
                workingSchedule={workingSchedule}
                scheduleData={scheduleData}
                onAssignmentComplete={onRefresh}
                isUpdating={isUpdating} />

              {/* Target metrics row */}
              <TargetMetricsRow
                metrics={scheduleData.metrics}
                scheduleData={scheduleData}
                weekStartDate={weekStartDate}
                workingSchedule={workingSchedule} />
            </>
          )}
        </tbody>
      </table>
    </div>
  )
}

// Memoize the component to prevent unnecessary re-renders when parent updates.
// Only re-render when surveyor, weekStartDate, weekEndDate, publicHolidaysByDate,
// scheduleData (parent-fed), or isUpdating change.
export default memo(SurveyorScheduleBlock, (prevProps, nextProps) => (
  // Custom comparison function for memo
  // Return true if props are equal (should NOT re-render)
  // Return false if props are different (should re-render)
  prevProps.surveyor?.id === nextProps.surveyor?.id
  && prevProps.weekStartDate?.getTime() === nextProps.weekStartDate?.getTime()
  && prevProps.weekEndDate?.getTime() === nextProps.weekEndDate?.getTime()
  && prevProps.publicHolidaysByDate === nextProps.publicHolidaysByDate
  && prevProps.scheduleData === nextProps.scheduleData
  && prevProps.refreshTrigger === nextProps.refreshTrigger
  && prevProps.isUpdating === nextProps.isUpdating
))
